import React, { useState, useEffect } from 'react';
import { 
  BarChart3, 
  TrendingUp, 
  Users, 
  FileText, 
  DollarSign,
  Calendar,
  Activity,
  Clock,
  ArrowUp,
  ArrowDown,
  Loader2,
  RefreshCw
} from 'lucide-react';
import api from '@/services/api';
import { toast } from 'sonner';

const AnalyticsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30d');
  const [usageData, setUsageData] = useState(null);
  const [revenueData, setRevenueData] = useState(null);
  const [actionsData, setActionsData] = useState(null);

  useEffect(() => {
    fetchAllAnalytics();
  }, [period]);

  const fetchAllAnalytics = async () => {
    setLoading(true);
    try {
      const [usage, revenue, actions] = await Promise.all([
        api.get(`/admin/analytics/usage?period=${period}`),
        api.get(`/admin/analytics/revenue?period=${period}`),
        api.get('/admin/analytics/popular-actions')
      ]);
      
      setUsageData(usage.data);
      setRevenueData(revenue.data);
      setActionsData(actions.data);
    } catch (error) {
      toast.error('Error cargando analytics');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, trend, color = 'blue' }) => (
    <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-slate-400 text-sm">{title}</p>
          <p className="text-2xl font-bold mt-1">{value}</p>
          {subtitle && <p className="text-slate-500 text-xs mt-1">{subtitle}</p>}
        </div>
        <div className={`p-2.5 rounded-lg bg-${color}-500/10`}>
          <Icon className={`w-5 h-5 text-${color}-400`} />
        </div>
      </div>
      {trend !== undefined && (
        <div className={`flex items-center mt-3 text-sm ${trend >= 0 ? 'text-green-400' : 'text-red-400'}`}>
          {trend >= 0 ? <ArrowUp className="w-4 h-4 mr-1" /> : <ArrowDown className="w-4 h-4 mr-1" />}
          <span>{Math.abs(trend)}% vs periodo anterior</span>
        </div>
      )}
    </div>
  );

  const SimpleBarChart = ({ data, label, maxValue }) => {
    const max = maxValue || Math.max(...data.map(d => d.count), 1);
    return (
      <div className="space-y-2">
        {data.slice(0, 8).map((item, idx) => (
          <div key={idx} className="flex items-center space-x-3">
            <span className="text-slate-400 text-xs w-24 truncate">{item.type || item.action || item.feature}</span>
            <div className="flex-1 h-6 bg-slate-800 rounded-md overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-blue-600 to-cyan-500 rounded-md flex items-center justify-end pr-2"
                style={{ width: `${(item.count / max) * 100}%` }}
              >
                <span className="text-xs font-medium">{item.count}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const TimelineChart = ({ data, color = 'blue' }) => {
    if (!data || data.length === 0) return <p className="text-slate-500 text-sm">Sin datos</p>;
    
    const max = Math.max(...data.map(d => d.count || d.revenue || 0), 1);
    const chartHeight = 100;
    
    return (
      <div className="relative h-28">
        <svg viewBox={`0 0 ${data.length * 20} ${chartHeight}`} className="w-full h-full">
          {/* Grid lines */}
          {[0, 25, 50, 75, 100].map(y => (
            <line 
              key={y} 
              x1="0" 
              y1={chartHeight - y} 
              x2={data.length * 20} 
              y2={chartHeight - y} 
              stroke="#334155" 
              strokeWidth="0.5" 
            />
          ))}
          
          {/* Area */}
          <path
            d={`M 0 ${chartHeight} ${data.map((d, i) => {
              const x = i * 20 + 10;
              const y = chartHeight - ((d.count || d.revenue || 0) / max) * (chartHeight - 10);
              return `L ${x} ${y}`;
            }).join(' ')} L ${(data.length - 1) * 20 + 10} ${chartHeight} Z`}
            fill={`url(#gradient-${color})`}
            opacity="0.3"
          />
          
          {/* Line */}
          <path
            d={`M ${data.map((d, i) => {
              const x = i * 20 + 10;
              const y = chartHeight - ((d.count || d.revenue || 0) / max) * (chartHeight - 10);
              return `${i === 0 ? '' : 'L'} ${x} ${y}`;
            }).join(' ')}`}
            fill="none"
            stroke={color === 'blue' ? '#3b82f6' : '#10b981'}
            strokeWidth="2"
          />
          
          {/* Points */}
          {data.map((d, i) => {
            const x = i * 20 + 10;
            const y = chartHeight - ((d.count || d.revenue || 0) / max) * (chartHeight - 10);
            return (
              <circle 
                key={i} 
                cx={x} 
                cy={y} 
                r="3" 
                fill={color === 'blue' ? '#3b82f6' : '#10b981'} 
              />
            );
          })}
          
          <defs>
            <linearGradient id={`gradient-${color}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color === 'blue' ? '#3b82f6' : '#10b981'} />
              <stop offset="100%" stopColor="transparent" />
            </linearGradient>
          </defs>
        </svg>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold flex items-center space-x-2">
          <BarChart3 className="w-5 h-5 text-blue-400" />
          <span>Analytics Avanzados</span>
        </h2>
        <div className="flex items-center space-x-3">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-sm"
          >
            <option value="7d">Últimos 7 días</option>
            <option value="30d">Últimos 30 días</option>
            <option value="90d">Últimos 90 días</option>
            <option value="365d">Último año</option>
          </select>
          <button
            onClick={fetchAllAnalytics}
            className="p-2 bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard 
          title="Ingresos Totales" 
          value={`$${revenueData?.total_revenue_usd || 0}`}
          subtitle={`${revenueData?.total_transactions || 0} transacciones`}
          icon={DollarSign}
          color="green"
        />
        <StatCard 
          title="Suscripciones Activas" 
          value={revenueData?.active_subscriptions?.total || 0}
          subtitle={`${revenueData?.active_subscriptions?.monthly || 0} mensuales, ${revenueData?.active_subscriptions?.annual || 0} anuales`}
          icon={Users}
          color="purple"
        />
        <StatCard 
          title="Tasa de Conversión" 
          value={`${revenueData?.conversion_rate || 0}%`}
          subtitle="Usuarios gratuitos → premium"
          icon={TrendingUp}
          color="blue"
        />
        <StatCard 
          title="Churn" 
          value={revenueData?.churned_subscriptions || 0}
          subtitle="Suscripciones expiradas"
          icon={Activity}
          color="red"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Contracts */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5 flex flex-col">
          <h3 className="text-sm font-medium text-slate-300 mb-4 flex items-center gap-2">
            <FileText className="w-4 h-4 text-blue-400" />
            <span>Contratos Creados por Día</span>
          </h3>
          <div className="flex-1 min-h-[120px]">
            <TimelineChart data={usageData?.daily_contracts} color="blue" />
          </div>
        </div>

        {/* Daily Users */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5 flex flex-col">
          <h3 className="text-sm font-medium text-slate-300 mb-4 flex items-center gap-2">
            <Users className="w-4 h-4 text-green-400" />
            <span>Nuevos Usuarios por Día</span>
          </h3>
          <div className="flex-1 min-h-[120px]">
            <TimelineChart data={usageData?.daily_users} color="green" />
          </div>
        </div>
      </div>

      {/* Popular Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Contract Types */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5 flex flex-col">
          <h3 className="text-sm font-medium text-slate-300 mb-4 flex items-center gap-2">
            <FileText className="w-4 h-4 text-blue-400" />
            <span>Tipos de Contrato Más Usados</span>
          </h3>
          <div className="flex-1 min-h-[200px]">
            <SimpleBarChart data={usageData?.contract_types || []} />
          </div>
        </div>

        {/* AI Features */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5 flex flex-col">
          <h3 className="text-sm font-medium text-slate-300 mb-4 flex items-center gap-2">
            <Activity className="w-4 h-4 text-purple-400" />
            <span>Uso de Funciones IA</span>
          </h3>
          <div className="flex-1 min-h-[200px]">
            <SimpleBarChart data={usageData?.ai_feature_usage || []} />
          </div>
        </div>

        {/* Popular Actions */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5 flex flex-col">
          <h3 className="text-sm font-medium text-slate-300 mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-green-400" />
            <span>Acciones Más Populares</span>
          </h3>
          <div className="flex-1 min-h-[200px]">
            <SimpleBarChart data={actionsData?.popular_actions || []} />
          </div>
        </div>
      </div>

      {/* Active Users Table */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5">
        <h3 className="text-sm font-medium text-slate-300 mb-4 flex items-center space-x-2">
          <Activity className="w-4 h-4" />
          <span>Usuarios Más Activos (Última Semana)</span>
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-slate-400 border-b border-slate-700">
                <th className="text-left py-2 px-3">Usuario</th>
                <th className="text-left py-2 px-3">Email</th>
                <th className="text-right py-2 px-3">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {actionsData?.most_active_users?.slice(0, 5).map((user, idx) => (
                <tr key={idx} className="border-b border-slate-800">
                  <td className="py-2 px-3">{user.username}</td>
                  <td className="py-2 px-3 text-slate-400">{user.email}</td>
                  <td className="py-2 px-3 text-right">
                    <span className="bg-blue-500/20 text-blue-300 px-2 py-0.5 rounded-full text-xs">
                      {user.actions} acciones
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Hourly Activity Pattern */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5">
        <h3 className="text-sm font-medium text-slate-300 mb-4 flex items-center space-x-2">
          <Clock className="w-4 h-4" />
          <span>Patrón de Actividad por Hora (Última Semana)</span>
        </h3>
        <div className="flex items-end justify-between h-32 gap-1">
          {(usageData?.hourly_activity || []).map((hour, idx) => {
            const maxHourly = Math.max(...(usageData?.hourly_activity || []).map(h => h.count), 1);
            const height = (hour.count / maxHourly) * 100;
            return (
              <div 
                key={idx} 
                className="flex-1 bg-gradient-to-t from-blue-600 to-cyan-400 rounded-t-sm transition-all hover:opacity-80"
                style={{ height: `${height}%`, minHeight: '4px' }}
                title={`${hour.hour}:00 - ${hour.count} acciones`}
              />
            );
          })}
        </div>
        <div className="flex justify-between mt-2 text-xs text-slate-500">
          <span>00:00</span>
          <span>06:00</span>
          <span>12:00</span>
          <span>18:00</span>
          <span>23:00</span>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
